import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Target,
  Heart,
  Users,
  Trophy,
  ArrowRight,
  CheckCircle,
} from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: "Resultaatgericht",
      description:
        "Wij focussen op meetbare resultaten. Elk programma is ontworpen om je dichter bij je doelen te brengen.",
    },
    {
      icon: Heart,
      title: "Persoonlijke Aandacht",
      description:
        "Geen one-size-fits-all aanpak. Elk traject wordt afgestemd op jouw unieke behoeften en mogelijkheden.",
    },
    {
      icon: Users,
      title: "Community",
      description:
        "Word onderdeel van een motiverende groep gelijkgestemden die elkaar pushen naar succes.",
    },
    {
      icon: Trophy,
      title: "Professionaliteit",
      description:
        "Gecertificeerde coaches met jarenlange ervaring in de fitness en gezondheidsbranche.",
    },
  ];

  return (
    <main data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="section bg-[#0A0A0A]" data-testid="about-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="caption mb-4 block">Over Arena Gym</span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
                Jouw Lichaam,
                <br />
                <span className="text-red-500">Ons Project</span>
              </h1>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Arena Private Gym is opgericht met één doel: mensen
                begeleiden naar hun beste fysieke vorm. Wij geloven dat fitness
                meer is dan alleen trainen - het is een levensstijl die je
                sterker maakt, zowel fysiek als mentaal.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                Gevestigd in het hart van Torhout, bieden wij een professionele
                omgeving waar kwaliteit en persoonlijke aandacht centraal staan.
                Of je nu man of vrouw bent, beginner of gevorderd - bij Arena Gym
                vind je de begeleiding die past bij jouw niveau en ambities.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-[3/4] rounded-sm overflow-hidden border border-zinc-800">
                  <img
                    src="/gallery-7.jpeg"
                    alt="Groepstraining"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-[3/4] rounded-sm overflow-hidden border border-zinc-800 mt-8">
                  <img
                    src="/gallery-4.jpeg"
                    alt="Personal Training"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-[#121212] border border-zinc-800 px-8 py-4 rounded-sm">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-black text-red-500 font-['Barlow_Condensed']">
                      10+
                    </div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">
                      Jaar Ervaring
                    </div>
                  </div>
                  <div className="w-px h-12 bg-zinc-700" />
                  <div className="text-center">
                    <div className="text-3xl font-black text-red-500 font-['Barlow_Condensed']">
                      500+
                    </div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">
                      Tevreden Leden
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section bg-[#121212]" data-testid="about-mission">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="caption mb-4 block">Onze Missie</span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-8 font-['Barlow_Condensed'] uppercase tracking-tighter">
                Meer Dan Alleen <span className="text-red-500">Training</span>
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed">
                Onze missie is om mensen te begeleiden naar hun beste fysieke
                vorm door middel van professionele training, voedingsadvies en
                mentale coaching. Wij geloven in een holistische benadering
                waarbij lichaam en geest samen sterker worden.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-sm hover:border-red-500/30 transition-all duration-500 group"
                  data-testid={`value-card-${index}`}
                >
                  <value.icon className="w-10 h-10 text-red-500 mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-black text-white mb-2 font-['Barlow_Condensed'] uppercase">
                    {value.title}
                  </h3>
                  <p className="text-zinc-400 text-sm">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section bg-[#0A0A0A]" data-testid="about-offer">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="aspect-video rounded-sm overflow-hidden border border-zinc-800">
                <img
                  src="/gym-interior-1.png"
                  alt="Arena Gym Interior"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <span className="caption mb-4 block">Wat Wij Bieden</span>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
                Coaching Voor{" "}
                <span className="text-red-500">Iedereen</span>
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-8">
                Bij Arena Gym bieden wij begeleiding voor mannen en vrouwen van alle
                niveaus. Of je nu wilt afvallen, spiermassa opbouwen, of gewoon
                fitter en gezonder wilt worden - wij hebben het juiste programma
                voor jou.
              </p>

              <ul className="space-y-4 mb-8">
                {[
                  "Personal training met individuele aandacht",
                  "Dynamische groepslessen voor extra motivatie",
                  "Professionele voedingsbegeleiding",
                  "Transformatietrajecten met meetbare resultaten",
                  "Diëtiste begeleiding voor optimale voeding",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-300">{item}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/diensten"
                data-testid="about-services-link"
                className="btn-primary inline-flex items-center gap-2"
              >
                Bekijk Onze Diensten
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-[#121212]" data-testid="about-cta">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Klaar om te beginnen?</span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Ontmoet Je <span className="text-red-500">Coach</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Jan staat klaar om jou te begeleiden naar jouw doelen. Maak
              kennis tijdens een intake gesprek.
            </p>
            <Link
              to="/team"
              data-testid="about-team-link"
              className="btn-primary inline-flex items-center gap-2"
            >
              Ontmoet Jan
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
